import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import { portalApi } from '../api';
import type { ContentFilters, ContentKind, PublicContentListOut } from '../types';
import { ContentCard } from './ContentCard';
import { ContentDetailOverlay } from './ContentDetailOverlay';

interface CatalogSectionProps {
  kind: Exclude<ContentKind, 'NEWS' | 'QUICK_POLL' | 'ACHIEVEMENT' | 'EVENT'>;
  filterKey?: string;
}

const HASH_TO_FILTER: Record<string, { category?: string; direction?: string; subtype?: string }> = {
  science: { category: 'science', direction: 'science' },
  culture: { direction: 'culture', category: 'culture' },
  innovation: { direction: 'innovation', category: 'innovation' },
  media: { direction: 'media', category: 'media' },
  sport: { direction: 'sport', category: 'sport' },
  society: { direction: 'society' },
  announcements: { category: 'announcements' },
  templates: { category: 'templates' },
  tests: { category: 'tests' },
  books: { category: 'books' },
  periodicals: { category: 'periodicals' },
  journals: { category: 'periodicals' },
  proceedings: { category: 'proceedings' },
  institutions: { category: 'institutions' },
  grants: { category: 'grants' },
  internships: { category: 'internships' },
  courses: { category: 'courses' },
  centers: { category: 'centers' },
  clubs: { category: 'clubs' },
  teaching: { direction: 'teaching' },
  it: { direction: 'it' },
  linguistics: { direction: 'linguistics' },
  language: { direction: 'linguistics' },
  government: { direction: 'government' },
  public: { direction: 'government' },
};

const META: Record<
  CatalogSectionProps['kind'],
  { title: string; eyebrow: string; desc: string }
> = {
  COMPETITION: {
    title: 'Конкурсы и возможности',
    eyebrow: 'Конкурсы',
    desc: 'Образование, культура, инновации, СМИ и спорт — с фильтрами по уровню и срокам.',
  },
  MATERIAL: {
    title: 'Материалы и ресурсы',
    eyebrow: 'Материалы',
    desc: 'Объявления, шаблоны, тесты, книги, журналы и сборники.',
  },
  EDUCATION: {
    title: 'Образование',
    eyebrow: 'Образование',
    desc: 'Гранты, стажировки, курсы, центры и институты.',
  },
  VACANCY: {
    title: 'Вакансии',
    eyebrow: 'Вакансии',
    desc: 'Педагогика, IT, лингвистика и госслужба.',
  },
};

const COMP_DIRS = [
  ['all', 'Все направления'],
  ['science', 'Наука'],
  ['culture', 'Культура'],
  ['innovation', 'Инновации'],
  ['media', 'СМИ'],
  ['sport', 'Спорт'],
  ['other', 'Другое'],
] as const;

const MATERIAL_TYPES = [
  ['all', 'Все типы'],
  ['announcements', 'Объявления'],
  ['templates', 'Шаблоны'],
  ['tests', 'Тесты'],
  ['books', 'Книги'],
  ['periodicals', 'Журналы'],
  ['proceedings', 'Сборники'],
] as const;

const EDU_CATS = [
  ['all', 'Все категории'],
  ['institutions', 'Институты'],
  ['grants', 'Гранты'],
  ['internships', 'Стажировки'],
  ['courses', 'Курсы'],
  ['centers', 'Центры'],
  ['clubs', 'Клубы'],
] as const;

const VAC_DIRS = [
  ['all', 'Все направления'],
  ['teaching', 'Педагогика'],
  ['it', 'IT'],
  ['linguistics', 'Лингвистика'],
  ['government', 'Госслужба'],
  ['other', 'Другое'],
] as const;

const LEVELS = ['Районный', 'Городской', 'Областной', 'Республиканский', 'Международный'];
const FORMATS_EVENT = ['Очно', 'Онлайн', 'Гибрид'];
const FORMATS_JOB = ['Очно', 'Удалённо', 'Гибрид'];
const EMPLOYMENT = ['Полная', 'Частичная', 'Проектная', 'Стажировка'];
const MATERIAL_FORMATS = ['PDF', 'DOCX', 'XLSX', 'PPTX', 'Image', 'Link', 'Other'];
const LANGUAGES = ['KZ', 'RU', 'EN'];
const PARTICIPANTS = ['Ученики', 'Студенты', 'Магистранты', 'Докторанты', 'Исследователи'];

const Field = ({
  label,
  children,
  wide,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) => (
  <label className={`public-field${wide ? ' public-field--wide' : ''}`}>
    {label}
    {children}
  </label>
);

export const CatalogSection = ({ kind, filterKey = '' }: CatalogSectionProps) => {
  const { t } = useI18n();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const isOwner = Boolean(user && hasRole('SYSTEM_OWNER'));
  const meta = META[kind];

  const hashFilter = useMemo(() => {
    if (!filterKey || filterKey === 'all' || filterKey === 'search') return {};
    return HASH_TO_FILTER[filterKey] ?? { category: filterKey, direction: filterKey };
  }, [filterKey]);

  const [filters, setFilters] = useState<ContentFilters>({
    kind,
    sort: 'new',
    page: 1,
    ...hashFilter,
  });
  const [q, setQ] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [data, setData] = useState<PublicContentListOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      kind,
      page: 1,
      category: hashFilter.category,
      direction: hashFilter.direction,
      subtype: hashFilter.subtype,
    }));
  }, [kind, hashFilter]);

  useEffect(() => {
    if (filterKey === 'search') searchRef.current?.focus();
  }, [filterKey]);

  const load = useCallback(() => {
    setLoading(true);
    portalApi
      .list({ ...filters, q: q || undefined })
      .then(setData)
      .finally(() => setLoading(false));
  }, [filters, q]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Partial<ContentFilters>) => setFilters((f) => ({ ...f, ...patch, page: 1 }));

  const reset = () => {
    setQ('');
    setFilters({ kind, sort: 'new', page: 1 });
    setAdvancedOpen(false);
  };

  const deadlineSelect = (
    <select
      value={filters.deadline_filter || ''}
      onChange={(e) => update({ deadline_filter: e.target.value || undefined })}
    >
      <option value="">Любой</option>
      <option value="ACTIVE">Актуальные</option>
      <option value="7D">≤ 7 дней</option>
      <option value="30D">≤ 30 дней</option>
      <option value="NO_DATE">Без даты</option>
      <option value="EXPIRED">Завершённые</option>
    </select>
  );

  const costSelect = (extra?: Array<[string, string]>) => (
    <select
      value={filters.cost_type || ''}
      onChange={(e) => update({ cost_type: e.target.value || undefined })}
    >
      <option value="">Любая</option>
      <option value="FREE">Бесплатно</option>
      <option value="PAID">Платно</option>
      {(extra || []).map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );

  const mainFilters = () => {
    if (kind === 'COMPETITION') {
      return (
        <>
          <Field label="Направление">
            <select
              value={filters.category || filters.direction || 'all'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'all') update({ category: undefined, direction: undefined });
                else update({ category: v, direction: v });
              }}
            >
              {COMP_DIRS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Уровень">
            <select
              value={filters.level || ''}
              onChange={(e) => update({ level: e.target.value || undefined })}
            >
              <option value="">Все уровни</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Формат">
            <select
              value={filters.format_type || ''}
              onChange={(e) => update({ format_type: e.target.value || undefined })}
            >
              <option value="">Любой</option>
              {FORMATS_EVENT.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Стоимость">{costSelect()}</Field>
          <Field label="Место">
            <input
              type="text"
              value={filters.city || ''}
              onChange={(e) => update({ city: e.target.value || undefined })}
              placeholder="Страна, регион, город"
            />
          </Field>
          <Field label="Дедлайн">{deadlineSelect}</Field>
        </>
      );
    }

    if (kind === 'MATERIAL') {
      return (
        <>
          <Field label="Тип">
            <select
              value={filters.category || 'all'}
              onChange={(e) => {
                const v = e.target.value;
                update({ category: v === 'all' ? undefined : v });
              }}
            >
              {MATERIAL_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Направление">
            <input
              type="text"
              value={filters.direction || ''}
              onChange={(e) => update({ direction: e.target.value || undefined })}
              placeholder="Гуманитарные науки, Data Science…"
            />
          </Field>
          <Field label="Язык">
            <select
              value={filters.language || ''}
              onChange={(e) => update({ language: e.target.value || undefined })}
            >
              <option value="">Все</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Доступ">{costSelect()}</Field>
          <Field label="Формат">
            <select
              value={filters.format_type || ''}
              onChange={(e) => update({ format_type: e.target.value || undefined })}
            >
              <option value="">Все</option>
              {MATERIAL_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
        </>
      );
    }

    if (kind === 'EDUCATION') {
      return (
        <>
          <Field label="Категория">
            <select
              value={filters.category || 'all'}
              onChange={(e) => {
                const v = e.target.value;
                update({ category: v === 'all' ? undefined : v });
              }}
            >
              {EDU_CATS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Направление">
            <input
              type="text"
              value={filters.direction || ''}
              onChange={(e) => update({ direction: e.target.value || undefined })}
              placeholder="Филология, исследования…"
            />
          </Field>
          <Field label="Место">
            <input
              type="text"
              value={filters.city || ''}
              onChange={(e) => update({ city: e.target.value || undefined })}
              placeholder="Страна, регион, город"
            />
          </Field>
          <Field label="Формат">
            <select
              value={filters.format_type || ''}
              onChange={(e) => update({ format_type: e.target.value || undefined })}
            >
              <option value="">Любой</option>
              {FORMATS_EVENT.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Стоимость / финансирование">
            {costSelect([
              ['GRANT', 'Грант'],
              ['SCHOLARSHIP', 'Стипендия'],
              ['FUNDED', 'Финансируется'],
            ])}
          </Field>
          <Field label="Дедлайн">{deadlineSelect}</Field>
        </>
      );
    }

    return (
      <>
        <Field label="Направление">
          <select
            value={filters.direction || 'all'}
            onChange={(e) => {
              const v = e.target.value;
              update({ direction: v === 'all' ? undefined : v });
            }}
          >
            {VAC_DIRS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Место">
          <input
            type="text"
            value={filters.city || ''}
            onChange={(e) => update({ city: e.target.value || undefined })}
            placeholder="Город, страна, Remote"
          />
        </Field>
        <Field label="Формат">
          <select
            value={filters.format_type || ''}
            onChange={(e) => update({ format_type: e.target.value || undefined })}
          >
            <option value="">Любой</option>
            {FORMATS_JOB.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Занятость">
          <select
            value={filters.employment_type || ''}
            onChange={(e) => update({ employment_type: e.target.value || undefined })}
          >
            <option value="">Любая</option>
            {EMPLOYMENT.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Дедлайн">{deadlineSelect}</Field>
      </>
    );
  };

  return (
    <div className="portal-section">
      <div className="public-hero">
        <div>
          <span className="public-eyebrow">{meta.eyebrow}</span>
          <h2>{meta.title}</h2>
          <p>{meta.desc}</p>
        </div>
        {isOwner ? (
          <button
            type="button"
            className="portal-small-action primary"
            onClick={() => navigate(`${routes.owner}/content`)}
          >
            + Добавить
          </button>
        ) : null}
      </div>

      <section className="portal-filter-panel">
        <header>
          <div>
            <h2>Поиск и фильтры</h2>
            <p>Фильтры применяются сразу; выбранное состояние сохраняется при открытии карточки.</p>
          </div>
          <div className="portal-filter-actions">
            {kind === 'COMPETITION' ? (
              <button
                type="button"
                className="portal-filter-toggle"
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                {advancedOpen ? 'Закрыть' : 'Фильтры'}
              </button>
            ) : null}
            <button type="button" className="portal-small-action" onClick={reset}>
              Сбросить
            </button>
          </div>
        </header>

        <div className="portal-filter-grid public-filters">
          <Field label="Поиск" wide>
            <input
              ref={searchRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder={t('portal.filter.searchPlaceholder')}
            />
          </Field>
          <Field label="Сортировка">
            <select
              value={filters.sort || 'new'}
              onChange={(e) => update({ sort: e.target.value })}
            >
              <option value="new">{t('portal.filter.sort.new' as any)}</option>
              <option value="popular">{t('portal.filter.sort.popular' as any)}</option>
              <option value="deadline">{t('portal.filter.sort.deadline' as any)}</option>
              {kind === 'MATERIAL' ? <option value="az">A–Z</option> : null}
            </select>
          </Field>
          {mainFilters()}
        </div>

        {kind === 'COMPETITION' && advancedOpen ? (
          <div className="portal-advanced public-filters">
            <Field label="Язык">
              <select
                value={filters.language || ''}
                onChange={(e) => update({ language: e.target.value || undefined })}
              >
                <option value="">Все</option>
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Участники">
              <select
                value={filters.subtype || ''}
                onChange={(e) => update({ subtype: e.target.value || undefined })}
              >
                <option value="">Все</option>
                {PARTICIPANTS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Вид конкурса">
              <input
                type="text"
                value={filters.category && !COMP_DIRS.some(([v]) => v === filters.category) ? filters.category : ''}
                onChange={(e) => update({ category: e.target.value || undefined })}
                placeholder="Проект, олимпиада, премия…"
              />
            </Field>
          </div>
        ) : null}
      </section>

      <section className="portal-result-head">
        <div>
          <strong>{data?.total ?? 0}</strong>
          <span>найдено</span>
        </div>
      </section>

      {loading ? (
        <div className="public-empty">Загрузка...</div>
      ) : !data || data.items.length === 0 ? (
        <div className="public-empty">{t('portal.filter.noResults' as any)}</div>
      ) : (
        <>
          <div className="public-results">
            {data.items.map((item) => (
              <ContentCard key={item.id} item={item} onClick={(i) => setOpenId(i.id)} />
            ))}
          </div>
          {data.has_more ? (
            <div className="portal-load-more">
              <button type="button" onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}>
                Загрузить ещё
              </button>
            </div>
          ) : null}
        </>
      )}

      {openId ? <ContentDetailOverlay contentId={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
};
